import React, { useCallback, useState } from 'react';
import { Button, Checkbox, Dropdown, Input, Tooltip, message } from 'antd';
import type { InputRef } from 'antd';
import { HolderOutlined, DownOutlined, PushpinOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MetricSelector } from '@src/components/common/CustomizeDropdown/styled';
import type { CustomizeDropdownProps } from '@src/components/common/CustomizeDropdown';

interface ColumnPreferences {
  selectedMetrics: string[];
  anchoredMetrics: Record<string, boolean>;
}

type Props = CustomizeDropdownProps & { onApply: (preferences: ColumnPreferences) => void };

function SortableColumn({
  metric,
  name,
  children,
}: {
  metric: string;
  name: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: metric,
  });
  return (
    <div
      ref={setNodeRef}
      data-column-id={metric}
      className="dimension-wrapper"
      style={{
        alignItems: 'center',
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
        background: isDragging ? '#f0f7ff' : undefined,
      }}
    >
      <Button
        type="text"
        size="small"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${name}`}
        icon={<HolderOutlined />}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      />
      {children}
    </div>
  );
}

export default function CustomizeColumns({
  allMetrics,
  selectedMetrics,
  anchoredMetrics,
  mandatoryDimensions = [],
  maxAnchorCount = 3,
  disabled,
  getPopupContainer,
  onApply,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(selectedMetrics);
  const [pins, setPins] = useState(anchoredMetrics);
  const [search, setSearch] = useState('');
  const focusSearch = useCallback((node: InputRef | null) => {
    if (node) node.focus({ preventScroll: true });
  }, []);
  const metadata = new Map(
    Object.values(allMetrics)
      .flat()
      .map((item) => [item.metric, item]),
  );
  const matches = (item) => item.name.toLowerCase().includes(search.toLowerCase());
  const changeOpen = (next: boolean) => {
    if (next) {
      setSelected([...selectedMetrics]);
      setPins({ ...anchoredMetrics });
      setSearch('');
    }
    setOpen(next);
  };
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setSelected((current) => {
      const from = current.indexOf(String(active.id));
      const to = current.indexOf(String(over.id));
      return from < 0 || to < 0 ? current : arrayMove(current, from, to);
    });
  };
  const toggle = (metric: string, checked: boolean) => {
    setSelected(checked ? [...selected, metric] : selected.filter((m) => m !== metric));
    if (!checked) setPins({ ...pins, [metric]: false });
  };
  const pin = (metric: string) => {
    if (!pins[metric] && selected.filter((m) => pins[m]).length >= maxAnchorCount) {
      message.warning(`Cannot pin more than ${maxAnchorCount} columns`);
      return;
    }
    setPins({ ...pins, [metric]: !pins[metric] });
  };
  const row = (item, isSelected = false) => {
    const content = (
      <>
        <Checkbox
          checked={selected.includes(item.metric)}
          disabled={mandatoryDimensions.includes(item.metric)}
          onChange={(event) => toggle(item.metric, event.target.checked)}
          style={{ flex: 1, minWidth: 0 }}
        >
          {item.name}
        </Checkbox>
        {isSelected && (
          <>
            <Tooltip
              title={
                pins[item.metric]
                  ? 'Unpin this column to let it scroll with the table.'
                  : `Pin this column to keep it visible on the left while scrolling horizontally. You can pin up to ${maxAnchorCount} columns.`
              }
            >
              <Button
                type={pins[item.metric] ? 'primary' : 'text'}
                size="small"
                aria-label={`${pins[item.metric] ? 'Unpin' : 'Pin'} ${item.name}`}
                aria-pressed={!!pins[item.metric]}
                icon={<PushpinOutlined />}
                onClick={() => pin(item.metric)}
              />
            </Tooltip>
          </>
        )}
      </>
    );
    return isSelected ? (
      <SortableColumn key={item.metric} metric={item.metric} name={item.name}>
        {content}
      </SortableColumn>
    ) : (
      <div key={item.metric} className="dimension-wrapper" style={{ alignItems: 'center' }}>
        {content}
      </div>
    );
  };
  const selectedRows = selected
    .map((metric, index) => ({ item: metadata.get(metric), index }))
    .filter(({ item }) => item && matches(item));
  const groups = Object.entries(allMetrics)
    .map(([name, items]) => ({
      name,
      items: items.filter((item) => !selected.includes(item.metric) && matches(item)),
    }))
    .filter(({ items }) => items.length);
  return (
    <Dropdown
      open={open}
      onOpenChange={changeOpen}
      disabled={disabled}
      destroyOnHidden
      trigger={['click']}
      getPopupContainer={getPopupContainer}
      popupRender={() => (
        <MetricSelector>
          <Input.Search
            ref={focusSearch}
            allowClear
            placeholder="Search Dimension / Metrics"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="menu-ul">
            {!!selectedRows.length && (
              <section aria-label="Selected columns">
                <div>Selected columns ({selected.length})</div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    items={selectedRows.map(({ item }) => item!.metric)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedRows.map(({ item }) => row(item, true))}
                  </SortableContext>
                </DndContext>
              </section>
            )}
            {groups.map(({ name, items }) => (
              <section key={name} aria-label={name}>
                <div>{name}</div>
                {items.map((item) => row(item))}
              </section>
            ))}
            {!selectedRows.length && !groups.length && <p>No matching dimensions or metrics.</p>}
          </div>
          <div className="bottom-space">
            <Button
              size="small"
              onClick={() => {
                setSelected(mandatoryDimensions.filter((m) => metadata.has(m)));
                setPins({});
              }}
            >
              Clear All
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                onApply({
                  selectedMetrics: selected,
                  anchoredMetrics: Object.fromEntries(
                    selected.filter((m) => pins[m]).map((m) => [m, true]),
                  ),
                });
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </MetricSelector>
      )}
    >
      <Button disabled={disabled}>
        Customize <DownOutlined />
      </Button>
    </Dropdown>
  );
}
